// auth.js — PR ② (SSO) core. Framework-free; React binds via AuthProvider.
//
// Implements the FE contract exactly:
//   §4 D19=A  access token in MEMORY ONLY; refresh token in localStorage,
//             overwritten IMMEDIATELY on every rotation (the old one is dead
//             server-side; replaying it revokes the whole session family).
//   §5        401(invalid_token) → single-flight refresh → retry ONCE.
//             Refresh reads localStorage AT CALL TIME (not a cached copy).
//   §5 multi-tab  `storage` listener: another tab's rotation is adopted
//             implicitly (read-at-call-time); another tab's sign-out clears
//             this tab too.
//   §7        soft gate: pre-flip the app works signed-out; the full sign-in
//             gate appears only when a 401 lands and no session can be made.

import { API_BASE } from './config';

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const REFRESH_KEY = 'mono.auth.refresh';
const AUTH_HINT_KEY = 'mono.auth.required';

/**
 * Whether we KNOW sign-in is required before any request has been made this
 * session. Two sources: REACT_APP_AUTH_REQUIRED=true (set at the flag flip —
 * deterministic, kills the pre-login dashboard flash even for first-ever
 * visitors) and a learned localStorage hint (persisted the first time this
 * browser ever sees a 401 invalid_token). Lets the shell route straight to
 * #/login instead of painting the dashboard and discovering auth via a 401.
 */
export function authRequiredKnown() {
  if (String(process.env.REACT_APP_AUTH_REQUIRED).toLowerCase() === 'true') return true;
  try { return localStorage.getItem(AUTH_HINT_KEY) === '1'; } catch { return false; }
}
function markAuthRequired() { try { localStorage.setItem(AUTH_HINT_KEY, '1'); } catch { /* private mode */ } }
const EXPIRY_MARGIN_S = 30;   // refresh this many seconds before nominal expiry

let accessToken = null;
let accessExpiresAt = 0;      // ms epoch
let currentUser = null;
let gateDemanded = false;     // a business 401 landed with no way to a session
let refreshInflight = null;   // single-flight promise
let initialized = false;
const listeners = new Set();

/* ── state + subscription ─────────────────────────────────────────── */

export function getAuthState() {
  return {
    user: currentUser,
    signedIn: !!currentUser,
    gate: gateDemanded && !currentUser,
  };
}

/**
 * Whether a stored session is about to be resumed — answerable SYNCHRONOUSLY,
 * before any effect has run. ⑭i.
 *
 * `initAuth` already knew this, but only from inside an effect, so the shell's
 * first paint was the dashboard and the splash arrived one commit later. React
 * unmounts what it is replacing: MainApp mounted, unmounted and remounted on
 * every single page load, and every bootstrap call it makes went out twice —
 * the first pass signed out. Measured on a live run as capabilities ×2,
 * skus ×2, order-lines ×2 before the user had touched anything.
 *
 * This is the SAME predicate `initAuth` branches on below, deliberately shared
 * rather than restated, so the shell and the resume cannot disagree about
 * whether a resume is coming.
 */
export function sessionResumable() {
  return Boolean(refreshInflight) || (!!getRefreshToken() && !currentUser);
}

export function onAuthChange(cb) { listeners.add(cb); return () => listeners.delete(cb); }
function emit() { const s = getAuthState(); listeners.forEach((cb) => cb(s)); }

/* ── storage ──────────────────────────────────────────────────────── */

function getRefreshToken() { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } }
function setRefreshToken(t) {
  try { if (t == null) localStorage.removeItem(REFRESH_KEY); else localStorage.setItem(REFRESH_KEY, t); } catch { /* private mode */ }
}

export function saveTokens(payload) {
  accessToken = payload.access_token || null;
  accessExpiresAt = Date.now() + Math.max(0, (payload.expires_in || 0) - EXPIRY_MARGIN_S) * 1000;
  if (payload.refresh_token) setRefreshToken(payload.refresh_token);   // §4: overwrite immediately
  // Both /auth/google and /auth/refresh return the same bundle (backend reply
  // §1.1 — one shared `_token_response()` helper, so they cannot drift). Silent
  // resume therefore recovers the user without a /auth/me round-trip, and a role
  // change propagates within one access-token lifetime (≤15 min) with no re-login.
  if (payload.user) currentUser = payload.user;
  gateDemanded = false;
  emit();
}

export function clearAuth({ demandGate = false } = {}) {
  accessToken = null; accessExpiresAt = 0; currentUser = null;
  setRefreshToken(null);
  if (demandGate) gateDemanded = true;
  emit();
}

export function demandSignIn() { gateDemanded = true; markAuthRequired(); emit(); }

/* ── lifecycle ────────────────────────────────────────────────────── */

/**
 * Call once at app start. Registers the multi-tab listener and, if a refresh
 * token survives from a prior session, resumes it (async). Returns true when
 * a resume was started.
 */
export function initAuth(apiBase) {
  // apiBase param kept for signature compatibility; the module reads config
  // directly so calls BEFORE this runs (child effects fire before parent
  // effects) already use the right base. Warn on drift, never depend on it.
  if (apiBase && apiBase !== API_BASE) {
    // eslint-disable-next-line no-console
    console.warn('initAuth: apiBase differs from config.API_BASE; using config.');
  }
  if (initialized) return false;   // StrictMode double-mount safe
  initialized = true;

  window.addEventListener('storage', (e) => {
    if (e.key !== REFRESH_KEY) return;
    if (e.newValue === null) {
      // Another tab signed out — drop this tab's session too (local only).
      accessToken = null; accessExpiresAt = 0; currentUser = null;
      emit();
    } else if (e.oldValue === null && e.newValue && !currentUser) {
      // Another tab signed IN while this tab had no session — adopt it by
      // refreshing (rotates; harmless: the newest pair lands in the shared
      // storage and every tab reads it at call time per §5).
      refreshTokens().catch(() => { /* cleared + emitted inside */ });
    }
    // A rotated value needs no other action: refresh reads storage at call time.
  });

  // ⑭i: one predicate, shared with the shell. See sessionResumable.
  if (!sessionResumable()) return false;
  if (refreshInflight) return true;                    // a boot call already started one
  // ⑭i: emit when the boot resume SETTLES, whatever the outcome.
  //
  // A transient failure — 404 route missing, 5xx, offline — deliberately does
  // NOT clearAuth (§5.4: the session must survive a mis-routed refresh), so it
  // emitted nothing, so nothing ever told the shell the resume was over. That
  // was survivable while the splash arrived a commit late and the dashboard had
  // already painted underneath it. Now that the splash is the FIRST paint, a
  // silent transient failure would be a permanent one. The app boots
  // signed-out, which is the documented pre-flip behaviour.
  refreshTokens()
    .catch(() => { /* handled inside: cleared+gated on 401/403, kept otherwise */ })
    .finally(() => { emit(); });
  return true;
}

/* ── refresh (single-flight, rotating) ────────────────────────────── */

export async function refreshTokens() {
  if (refreshInflight) return refreshInflight;   // §5.1: queue behind one flight
  refreshInflight = (async () => {
    const rt = getRefreshToken();                // §5.2: read AT CALL TIME
    if (!rt) {
      const err = new Error('no_refresh_token'); err.code = 'no_refresh_token';
      gateDemanded = true; emit();
      throw err;
    }
    // Request shape CONFIRMED by backend (reply §1.2): JSON body, field name
    // `refresh_token`. No cookie or header variant — sending `{token}` 422s.
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!resp.ok) {
      let body = null; try { body = await resp.json(); } catch { /* non-JSON */ }
      const code = body?.detail?.code || null;
      const err = new Error(code || `refresh_failed_${resp.status}`);
      err.code = code; err.status = resp.status; err.body = body;
      // §5.4 / §6: ONLY 401/403 (invalid_refresh · reuse_detected · inactive)
      // mean the session is gone — clear + gate for those. Anything else
      // (404 route missing, 5xx, edge/tunnel trouble) is TRANSIENT: keep the
      // refresh token so the session survives and a later attempt can succeed.
      // The old behaviour cleared on any !ok — a mis-routed 404 logged the
      // user out on every hard refresh.
      if (resp.status === 401 || resp.status === 403) {
        clearAuth({ demandGate: true });
      }
      throw err;
    }
    const payload = await resp.json();
    saveTokens(payload);                          // rotates refresh immediately
    return payload;
  })();
  try { return await refreshInflight; }
  finally { refreshInflight = null; }
}

/** Valid access token, refreshing first if expired/absent and possible. */
export async function ensureFreshAccess() {
  if (accessToken && Date.now() < accessExpiresAt) return accessToken;
  if (getRefreshToken()) { try { await refreshTokens(); } catch { /* handled */ } }
  return accessToken;
}

export function getAccessToken() { return accessToken; }

/* ── the request layer (§5 choreography) ──────────────────────────── */

export async function authFetch(url, options = {}) {
  const doFetch = async () => {
    const headers = new Headers(options.headers || {});
    const tok = await ensureFreshAccess();
    if (tok) headers.set('Authorization', `Bearer ${tok}`);
    return fetch(url, { ...options, headers });
  };

  let resp = await doFetch();
  if (resp.status === 401) {
    let code = null;
    try { code = (await resp.clone().json())?.detail?.code; } catch { /* non-JSON */ }
    if (code === 'invalid_token') {
      markAuthRequired();   // enforcement observed — future boots go straight to /login
      if (getRefreshToken()) {
        try {
          await refreshTokens();
          resp = await doFetch();                 // §5.3: retry ONCE, never loop
        } catch { /* cleared + gated inside refreshTokens */ }
      } else {
        // Flag flipped and we have no session at all → show the gate.
        demandSignIn();
      }
    }
  }
  return resp;
}

/* ── sign-in / sign-out ───────────────────────────────────────────── */

export async function signInWithGoogleCredential(idToken) {
  const resp = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  let body = null; try { body = await resp.json(); } catch { /* non-JSON */ }
  if (!resp.ok) {
    const err = new Error(body?.detail?.code || `auth_failed_${resp.status}`);
    err.code = body?.detail?.code || null; err.status = resp.status; err.body = body;
    throw err;
  }
  saveTokens(body);
  return body.user;
}

/** Sign out of this browser only (clears local session across tabs). */
export function signOutLocal() { clearAuth(); }

/** Revoke the whole session family server-side, then clear locally. */
export async function signOutEverywhere() {
  try { await authFetch(`${API_BASE}/auth/logout-all`, { method: 'POST' }); } catch { /* best effort */ }
  clearAuth();
}

/* ── util ─────────────────────────────────────────────────────────── */

/**
 * Decode a JWT payload for DISPLAY only (e.g. the email in the
 * not_provisioned message). No verification — the backend verifies.
 */
export function decodeJwtPayload(jwt) {
  try {
    const b64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch { return null; }
}
